import {
  array,
  boolean,
  exactOptional,
  literal,
  number,
  object,
  picklist,
  pipe,
  string,
  transform,
  type InferInput,
  type InferOutput,
} from "valibot";
import { compoundKey } from "@supply25/database/key";

export const EvidenceScanStatus = {
  PENDING: "PENDING",
  CLEAN: "CLEAN",
  INFECTED: "INFECTED",
  FAILED: "FAILED",
} as const;

export type EvidenceScanStatus =
  (typeof EvidenceScanStatus)[keyof typeof EvidenceScanStatus];

// Whichever of the two racing writers arrives first creates the record, so
// each writer stamps these shared fields
const baseEntries = {
  type: literal("EVIDENCE"),
  scanStatus: picklist(Object.values(EvidenceScanStatus)),
  s3Key: string(),
  createdDate: string(),
  lastUpdated: string(),
};

// The record as written by the upload-confirm redirect, once the browser has
// posted the file to S3 — its extra fields are absent while the scan wins
const confirmEntries = {
  ...baseEntries,
  uploaded: exactOptional(boolean()),
  /** Which UPLOAD additional question the file belongs to */
  additionalQuestionId: exactOptional(string()),
  authorId: exactOptional(string()),
  /** Also enriched from S3 object metadata for the no-JS upload path */
  fileName: exactOptional(string()),
};

// The record as written by the scan-result lambda — its extra fields come
// from S3 object metadata after a clean scan, so infected files never get them
const scanResultEntries = {
  ...baseEntries,
  contentType: exactOptional(string()),
  size: exactOptional(number()),
};

// A stored record is the union of the two writers' shapes
const evidenceEntries = {
  ...confirmEntries,
  ...scanResultEntries,
};

// The evidence shape as stored in the DB
const EvidenceDbSchema = object({
  ...evidenceEntries,
  pk: compoundKey("ORGANISATION", "WORKSPACE"),
  sk: compoundKey("URLID", "QUESTION", "EVIDENCE"),
});

export const EvidenceOutSchema = object({
  ...evidenceEntries,
  organisationId: string(),
  workspaceId: string(),
  urlId: string(),
  questionId: string(),
  fileId: string(),
});

// Take the stored DB shape and parse out the keys so app code can use them
export const EvidenceSchema = pipe(
  EvidenceDbSchema,
  transform(
    ({ pk, sk, ...rest }) =>
      ({
        ...rest,
        organisationId: pk.ORGANISATION,
        workspaceId: pk.WORKSPACE,
        urlId: sk.URLID,
        questionId: sk.QUESTION,
        fileId: sk.EVIDENCE,
      }) satisfies InferInput<typeof EvidenceOutSchema>,
  ),
);

export type Evidence = InferOutput<typeof EvidenceSchema>;
/** The stored item shape, with pk/sk still encoded as strings */
export type EvidenceFile = InferInput<typeof EvidenceSchema>;
export const EvidencesSchema = array(EvidenceSchema);

export const evidenceKey = {
  pk: EvidenceDbSchema.entries.pk,
  sk: EvidenceDbSchema.entries.sk,
};

/** Identifies one evidence file — the decoded segments of its item keys */
export type EvidenceFileRef = {
  urlId: string;
  questionId: string;
  fileId: string;
  organisationId: string;
  workspaceId: string;
};
